/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListUnknownSkillActivationsRequest,
  ListUnknownSkillActivationsSecurity,
} from "../models/operations/listunknownskillactivations.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildUnknownSkillActivationsInfiniteQuery,
  buildUnknownSkillActivationsQuery,
  prefetchUnknownSkillActivations,
  prefetchUnknownSkillActivationsInfinite,
  queryKeyUnknownSkillActivations,
  queryKeyUnknownSkillActivationsInfinite,
  UnknownSkillActivationsInfiniteQueryData,
  UnknownSkillActivationsPageParams,
  UnknownSkillActivationsQueryData,
} from "./unknownSkillActivations.core.js";
export {
  buildUnknownSkillActivationsInfiniteQuery,
  buildUnknownSkillActivationsQuery,
  prefetchUnknownSkillActivations,
  prefetchUnknownSkillActivationsInfinite,
  queryKeyUnknownSkillActivations,
  queryKeyUnknownSkillActivationsInfinite,
  type UnknownSkillActivationsInfiniteQueryData,
  type UnknownSkillActivationsPageParams,
  type UnknownSkillActivationsQueryData,
};

export type UnknownSkillActivationsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listUnknownActivations skills
 *
 * @remarks
 * List terminal skill activations that could not be attributed to a skill version.
 */
export function useUnknownSkillActivations(
  request?: ListUnknownSkillActivationsRequest | undefined,
  security?: ListUnknownSkillActivationsSecurity | undefined,
  options?: QueryHookOptions<
    UnknownSkillActivationsQueryData,
    UnknownSkillActivationsQueryError
  >,
): UseQueryResult<
  UnknownSkillActivationsQueryData,
  UnknownSkillActivationsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildUnknownSkillActivationsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listUnknownActivations skills
 *
 * @remarks
 * List terminal skill activations that could not be attributed to a skill version.
 */
export function useUnknownSkillActivationsSuspense(
  request?: ListUnknownSkillActivationsRequest | undefined,
  security?: ListUnknownSkillActivationsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    UnknownSkillActivationsQueryData,
    UnknownSkillActivationsQueryError
  >,
): UseSuspenseQueryResult<
  UnknownSkillActivationsQueryData,
  UnknownSkillActivationsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildUnknownSkillActivationsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listUnknownActivations skills
 *
 * @remarks
 * List terminal skill activations that could not be attributed to a skill version.
 */
export function useUnknownSkillActivationsInfinite(
  request?: ListUnknownSkillActivationsRequest | undefined,
  security?: ListUnknownSkillActivationsSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    UnknownSkillActivationsInfiniteQueryData,
    UnknownSkillActivationsQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<
    UnknownSkillActivationsInfiniteQueryData,
    UnknownSkillActivationsPageParams
  >,
  UnknownSkillActivationsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    UnknownSkillActivationsInfiniteQueryData,
    UnknownSkillActivationsQueryError,
    InfiniteData<
      UnknownSkillActivationsInfiniteQueryData,
      UnknownSkillActivationsPageParams
    >,
    QueryKey,
    UnknownSkillActivationsPageParams
  >({
    ...buildUnknownSkillActivationsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listUnknownActivations skills
 *
 * @remarks
 * List terminal skill activations that could not be attributed to a skill version.
 */
export function useUnknownSkillActivationsInfiniteSuspense(
  request?: ListUnknownSkillActivationsRequest | undefined,
  security?: ListUnknownSkillActivationsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    UnknownSkillActivationsInfiniteQueryData,
    UnknownSkillActivationsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<
    UnknownSkillActivationsInfiniteQueryData,
    UnknownSkillActivationsPageParams
  >,
  UnknownSkillActivationsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    UnknownSkillActivationsInfiniteQueryData,
    UnknownSkillActivationsQueryError,
    InfiniteData<
      UnknownSkillActivationsInfiniteQueryData,
      UnknownSkillActivationsPageParams
    >,
    QueryKey,
    UnknownSkillActivationsPageParams
  >({
    ...buildUnknownSkillActivationsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setUnknownSkillActivationsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: UnknownSkillActivationsQueryData,
): UnknownSkillActivationsQueryData | undefined {
  const key = queryKeyUnknownSkillActivations(...queryKeyBase);

  return client.setQueryData<UnknownSkillActivationsQueryData>(key, data);
}

export function invalidateUnknownSkillActivations(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "skills",
      "listUnknownActivations",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllUnknownSkillActivations(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "listUnknownActivations"],
  });
}
